import React, { forwardRef } from "react";
import {
  formatCNPJ,
  formatCPF,
  formatNumber,
  formatDateSystem,
} from "../../assets/utils";

// Usamos forwardRef para que o componente possa receber uma ref do componente pai
const PDFReportTemplate = forwardRef(({ reportData }, ref) => {
  if (!reportData) return null;

  const totalProfit = reportData.contracts.reduce(
    (acc, item) => acc + item.profitInYearValue,
    0
  );

  const styles = {
    container: {
      width: "210mm",
      padding: "15mm 20mm",
      boxSizing: "border-box",
      backgroundColor: "#fff",
      color: "#2c3e50",
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
      fontSize: "10pt",
      lineHeight: "1.5",
    },
    header: {
      textAlign: "center",
      marginBottom: "30px",
      paddingBottom: "20px",
      borderBottom: "3px solid #1a7f6d",
      position: "relative",
    },
    logo: {
      maxWidth: "90px",
      height: "auto",
      marginBottom: "12px",
      display: "block",
      margin: "0 auto 12px auto",
    },
    title: {
      fontSize: "20pt",
      margin: "0",
      color: "#1a7f6d",
      fontWeight: "600",
      letterSpacing: "-0.5px",
    },
    clientDetails: {
      marginBottom: "28px",
      padding: "18px 20px",
      border: "1px solid #e0e0e0",
      borderRadius: "6px",
      backgroundColor: "#f8fafb",
      boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
    },
    clientInfo: {
      margin: "8px 0",
      fontSize: "10pt",
      color: "#2c3e50",
    },
    clientInfoLabel: {
      fontWeight: "600",
      color: "#1a7f6d",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "9pt",
      marginBottom: "20px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      borderRadius: "6px",
      overflow: "hidden",
    },
    th: {
      backgroundColor: "#1a7f6d",
      padding: "12px 10px",
      border: "none",
      textAlign: "left",
      fontWeight: "600",
      color: "#fff",
      fontSize: "9pt",
    },
    td: {
      padding: "11px 10px",
      borderBottom: "1px solid #e8e8e8",
      color: "#2c3e50",
    },
    tbody_tr: {
      backgroundColor: "#fff",
    },
    tbody_tr_hover: {
      backgroundColor: "#f5f9f8",
    },
    tfoot: {
      fontWeight: "600",
      fontSize: "11pt",
      color: "#1a7f6d",
    },
    footerRow: {
      backgroundColor: "#e8f5f2",
      fontWeight: "600",
      color: "#1a7f6d",
      borderTop: "2px solid #1a7f6d",
    },
    footer: {
      marginTop: "50px",
      paddingTop: "25px",
      textAlign: "center",
      fontSize: "9pt",
      color: "#666",
      borderTop: "2px solid #1a7f6d",
    },
    signatureSection: {
      marginBottom: "25px",
      paddingBottom: "20px",
      borderBottom: "1px solid #e0e0e0",
    },
    signatureImage: {
      maxWidth: "160px",
      height: "auto",
      marginBottom: "12px",
    },
    companyInfo: {
      fontSize: "11pt",
      fontWeight: "600",
      margin: "8px 0 0 0",
      color: "#1a7f6d",
      letterSpacing: "0.3px",
    },
    cnpj: {
      fontSize: "9pt",
      color: "#666",
      margin: "4px 0 8px 0",
      fontFamily: "monospace",
    },
    signedLabel: {
      fontSize: "8pt",
      color: "#888",
      margin: "8px 0 0 0",
      fontStyle: "italic",
    },
    generatedDate: {
      fontSize: "8pt",
      color: "#999",
      marginTop: "12px",
    },
    companyHeaderInfo: {
      fontSize: "9pt",
      color: "#666",
      margin: "4px 0",
      fontFamily: "monospace",
    },
    companyHeaderName: {
      fontSize: "11pt",
      fontWeight: "600",
      color: "#1a7f6d",
      margin: "0 0 2px 0",
      letterSpacing: "0.3px",
    },
  };

  return (
    <div ref={ref} style={styles.container}>
      <header style={styles.header}>
        <img
          src="/logo-golden.png"
          alt="Logo Golden Brasil"
          style={styles.logo}
        />
        <p style={styles.companyHeaderName}>Holding Golden Brasil S/A</p>
        <p style={styles.companyHeaderInfo}>CNPJ: 42.007.698/0001-17</p>
        <h1 style={styles.title}>
          Relatório de Valorização Anual - {reportData.reportYear}
        </h1>
      </header>

      <section style={styles.clientDetails}>
        <p style={styles.clientInfo}>
          <span style={styles.clientInfoLabel}>Cliente:</span> {reportData.clientName}
        </p>
        <p style={styles.clientInfo}>
          <span style={styles.clientInfoLabel}>Documento:</span>{" "}
          {reportData.clientDocument.length === 11
            ? formatCPF(reportData.clientDocument)
            : formatCNPJ(reportData.clientDocument)}
        </p>
        <p style={styles.clientInfo}>
          <span style={styles.clientInfoLabel}>Endereço:</span>{" "}
          {reportData.clientAddress || "Não informado"}
        </p>
      </section>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Contrato ID</th>
            <th style={styles.th}>Valor de Compra</th>
            <th style={styles.th}>Data da Compra</th>
            <th style={styles.th}>Início Valorização</th>
            <th style={styles.th}>Valorização no Período (R$)</th>
          </tr>
        </thead>
        <tbody>
          {reportData.contracts.map((item) => (
            <tr key={item.contractId}>
              <td style={styles.td}>#{item.contractId}</td>
              <td style={styles.td}>R${formatNumber(item.totalPrice)}</td>
              <td style={styles.td}>{formatDateSystem(item.dateCreated)}</td>
              <td style={styles.td}>
                {formatDateSystem(item.firstValorization)}
              </td>
              <td style={styles.td}>
                R$ {formatNumber(item.profitInYearValue)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot style={styles.tfoot}>
          <tr>
            <td colSpan="3" style={{ ...styles.td, ...styles.footerRow }}>
              VALORIZAÇÃO TOTAL EM {reportData.reportYear}
            </td>
            <td style={{ ...styles.td, ...styles.footerRow, textAlign: "right" }}>
              R$ {formatNumber(totalProfit)}
            </td>
          </tr>
        </tfoot>
      </table>

      <footer style={styles.footer}>
        <div style={styles.signatureSection}>
          <img
            src="/golden-assinatura.png"
            alt="Assinatura Digital"
            style={styles.signatureImage}
          />
          <p style={styles.companyInfo}>Holding Golden Brasil S/A</p>
          <p style={styles.cnpj}>CNPJ: 42.007.698/0001-17</p>
          <p style={styles.signedLabel}>✓ Documento assinado digitalmente</p>
        </div>
        <p style={styles.generatedDate}>
          Gerado em: {new Date().toLocaleString("pt-BR")}
        </p>
      </footer>
    </div>
  );
});

export default PDFReportTemplate;
